<template>
  <UiComponentWrapper class="page">
    <!-- Section > Progress Bar -->
    <UiComponentWrapper class="progress">
      <UiComponentWrapper
        class="bar"
        :style="{ width: `${readingProgressInPercentage}%` }"
      />
    </UiComponentWrapper>
    <!-- End -->

    <!-- Section > Hero -->
    <UiComponentContainer
      class="jumbotron"
      :class="{ centered: data.page.isOfTypeExample || data.page.video }"
    >
      <!-- Content -->
      <UiComponentWrapper class="hero">
        <!-- Hero -->
        <NuxtLink
          class="back"
          :prefetch="false"
          :to="localePath('customers')"
        >
          <UiComponentIcon type="arrow-left" />{{ data.hub.back }}
        </NuxtLink>
        <UiComponentImage
          v-if="data.page.logo"
          :alt="data.page.logo.alt"
          class="logo"
          :expected-size="40"
          :extra-params="{
            trim: 'color',
          }"
          :height="data.page.logo.height"
          :orientation="
            data.page.logo.width > data.page.logo.height
              ? 'portrait'
              : 'landscape'
          "
          pad-width
          :source="data.page.logo.url"
          use-natural-height
          use-natural-width
          :width="data.page.logo.width"
        />
        <UiComponentHeading>{{ data.page.title }}</UiComponentHeading>
        <UiComponentParagraph>{{ data.page.description }}</UiComponentParagraph>
        <!-- Conditional > Information -->
        <!-- Displayed if it is a classic story -->
        <UiComponentWrapper
          v-if="!data.page.isOfTypeExample"
          class="information"
        >
          <UiComponentWrapper class="item">
            <UiComponentParagraph>{{ data.hub.industry }}</UiComponentParagraph>
            <UiComponentParagraph>{{ data.page.industry.name }}</UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="item">
            <UiComponentParagraph>{{ data.hub.size }}</UiComponentParagraph>
            <UiComponentParagraph>{{ data.page.size.name }}</UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="item">
            <UiComponentParagraph>{{ data.hub.location }}</UiComponentParagraph>
            <UiComponentParagraph>{{ data.page.location.name }}</UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentWrapper class="item">
            <UiComponentParagraph>{{ data.hub.useCase }}</UiComponentParagraph>
            <UiComponentParagraph>{{ data.page.useCases[0].name }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <!-- Media -->
      <UiComponentWrapper class="media">
        <OrganismPlayerVideo
          v-if="data.page.video"
          :poster-src="data.page.video.asset.thumbnailUrl"
          :video-src="data.page.video.asset.streamingUrl"
        />
        <UiComponentImage
          v-else-if="
            data.page.isOfTypeExample && data.page.image && heroImageWidth
          "
          :key="`example-story-image-${heroImageWidth}`"
          :alt="data.page.image.alt"
          class="picture"
          :expected-size="heroImageWidth"
          :orientation="
            data.page.image.height > data.page.image.width
              ? 'portrait'
              : 'landscape'
          "
          :height="data.page.image.height"
          :source="data.page.image.url"
          :width="data.page.image.width"
        />
        <UiComponentImage
          v-else-if="data.page.image && heroImageHeight && heroImageWidth"
          :key="`classic-story-image-${heroImageHeight}-${heroImageWidth}`"
          :alt="data.page.image.alt"
          class="picture"
          :expected-size="heroImageWidth"
          :extra-params="{
            fit: 'crop',
            h: heroImageHeight,
            w: heroImageWidth,
          }"
          :orientation="
            heroImageHeight > heroImageWidth ? 'portrait' : 'landscape'
          "
          :height="heroImageHeight"
          :source="data.page.image.url"
          use-natural-height
          use-natural-width
          :width="heroImageWidth"
        />
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Content -->
    <UiComponentContainer class="content">
      <!-- Table of Content (Conditional) -->
      <!-- Displayed only when the device width < 1024 -->
      <UiComponentWrapper
        v-if="tableOfContentEntries.length > 1"
        class="header"
      >
        <UiComponentParagraph>{{ data.hub.tableOfContent }}</UiComponentParagraph>
        <UiComponentSelect
          id="select"
          :key="`table-of-content-${tableOfContentIndex}`"
          :index="tableOfContentIndex"
          :options="tableOfContentEntries.map(({ textContent }) => textContent)"
          @select:change="
            scrollToTheSelectedElement(
              tableOfContentEntries.findIndex(
                ({ textContent }) => textContent === $event,
              ),
            )
          "
        />
      </UiComponentWrapper>
      <!-- End -->

      <!-- Main -->
      <UiComponentWrapper class="main">
        <UiComponentWrapper
          v-for="{
            id,
            __typename: typeOfContent,
            ...props
          } in data.page.content"
          :key="`section-${id}`"
          class="section"
          :class="{
            'events': typeOfContent === 'ContentUpcomingEventRecord',
            'features': typeOfContent === 'ContentFavoriteFeatureRecord',
            'get-started': typeOfContent === 'MoleculeSmallCallToActionRecord',
            'image': typeOfContent === 'ImageRecord',
            'key-numbers': typeOfContent === 'ContentKeyNumberRecord',
            'product-feature': typeOfContent === 'ContentProductFeatureRecord',
            'quote': typeOfContent === 'ContentQuoteRecord',
            'tag': typeOfContent === 'ContentTagRecord',
            'text': typeOfContent === 'TextRecord',
            'tl-dr': typeOfContent === 'ContentTlDrRecord',
            'video': typeOfContent === 'VideoRichRecord',
          }"
        >
          <!-- Content > Event -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ContentUpcomingEventRecord'"
            class="inner"
          >
            <iframe
              width="100%"
              :height="eventIframeHeight"
              frameborder="0"
              :src="`https://${publicRuntimeConfig.appBaseUrl}/${props.slug}/upcoming?upcoming_locale=${locale.value}&lang=${locale.value}`"
            />
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Features -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ContentFavoriteFeatureRecord'"
            class="inner"
          >
            <UiComponentImage
              v-if="data.hub.favoriteFeaturesImage"
              :alt="data.hub.favoriteFeaturesImage.alt"
              class="background"
              :expected-size="253"
              :height="data.hub.favoriteFeaturesImage.height"
              orientation="portrait"
              :source="data.hub.favoriteFeaturesImage.url"
              :width="data.hub.favoriteFeaturesImage.width"
            />
            <UiComponentHeading
              as="paragraph"
              :level="4"
            >
              {{
                $interpolate(data.hub.favoriteFeaturesTitle, {
                  "%{name}": data.page.name,
                })
              }}
            </UiComponentHeading>
            <UiComponentWrapper class="list">
              <UiComponentWrapper
                v-for="{ id: featureId, title, text } in props.features"
                :key="`feature-${id}-${featureId}`"
                class="item"
              >
                <UiComponentHeading
                  as="paragraph"
                  :level="5"
                >
                  {{ title }}
                </UiComponentHeading>
                <UiComponentParagraph>{{ text }}</UiComponentParagraph>
              </UiComponentWrapper>
              <AtomButton
                :to="localePath('features')"
                type="text"
              >
                {{ data.hub.favoriteFeaturesCta
                }}<UiComponentIcon type="arrow-right" />
              </AtomButton>
            </UiComponentWrapper>
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Image -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ImageRecord' && props.image"
            class="inner"
          >
            <UiComponentImage
              :key="`content-image-${id}-${contentImagesWidth}`"
              :alt="props.image.alt"
              :expected-size="contentImagesWidth"
              :height="props.image.height"
              :source="props.image.url"
              :width="props.image.width"
            />
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Key Numbers -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ContentKeyNumberRecord'"
            class="inner"
          >
            <UiComponentImage
              v-if="data.hub.keyNumbersImage"
              :alt="data.hub.keyNumbersImage.alt"
              class="background"
              :expected-size="240"
              :height="data.hub.keyNumbersImage.height"
              :source="data.hub.keyNumbersImage.url"
              :width="data.hub.keyNumbersImage.width"
            />
            <UiComponentHeading
              as="paragraph"
              :level="4"
            >
              {{
                props.title
              }}
            </UiComponentHeading>
            <UiComponentWrapper class="list">
              <UiComponentWrapper
                v-for="{ id: keyNumberId, title, text } in props.keyNumbers"
                :key="`feature-${id}-${keyNumberId}`"
                class="item"
              >
                <UiComponentParagraph>{{ title }}</UiComponentParagraph>
                <UiComponentParagraph>{{ text }}</UiComponentParagraph>
              </UiComponentWrapper>
            </UiComponentWrapper>
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Product Feature -->
          <UiComponentWrapper
            v-if="
              typeOfContent === 'ContentProductFeatureRecord' && props.feature
            "
            class="inner"
          >
            <MoleculeBlogFeature
              :content="{
                button: signUpOrUpdateCallToAction,
                image: props.feature.imageOnlyUsedForWowFeatures,
                text: props.feature.productAnnouncementBlockText,
                title: props.feature.productAnnouncementBlockTitle,
              }"
            />
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Quote -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ContentQuoteRecord'"
            class="inner"
          >
            <div v-html="props.text" />
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Small Call To Action -->
          <UiComponentWrapper
            v-if="typeOfContent === 'MoleculeSmallCallToActionRecord'"
            class="inner"
          >
            <MoleculeCallToAction
              :call-to-action="{
                ...signUpOrUpdateCallToAction,
                label: 'Small section CTA',
              }"
              :pattern-desktop="data.componentSmallCallToAction.patternDesktop"
              :pattern-mobile="data.componentSmallCallToAction.patternMobile"
            >
              <template #heading>
                {{
                  props.title || data.componentSmallCallToAction.title
                }}
              </template>
              <template #paragraph>
                {{
                  props.text || data.componentSmallCallToAction.text
                }}
              </template>
            </MoleculeCallToAction>
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Tag -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ContentTagRecord'"
            class="inner"
          >
            <UiComponentTag type="secondary">
              {{ props.name }}
            </UiComponentTag>
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Text -->
          <div
            v-if="typeOfContent === 'TextRecord'"
            class="inner"
            v-html="$md.render(props.text)"
          />
          <!-- End -->

          <!-- Content > TL;DR -->
          <UiComponentWrapper
            v-if="typeOfContent === 'ContentTlDrRecord'"
            class="inner"
          >
            <UiComponentHeading
              as="paragraph"
              :level="4"
            >
              {{
                props.title
              }}
            </UiComponentHeading>
            <div v-html="props.text" />
          </UiComponentWrapper>
          <!-- End -->

          <!-- Content > Video -->
          <UiComponentWrapper
            v-if="typeOfContent === 'VideoRichRecord'"
            class="inner"
          >
            <iframe
              width="100%"
              height="100%"
              :src="`https://www.youtube.com/embed/${props.video.providerUid}`"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            />
          </UiComponentWrapper>
          <!-- End -->
        </UiComponentWrapper>
      </UiComponentWrapper>
      <!-- End -->

      <!-- Aside -->
      <UiComponentWrapper class="aside">
        <!-- Quote (Conditional) -->
        <!-- Displayed only if the quote exists -->
        <UiComponentWrapper
          v-if="data.page.quote"
          class="quote"
        >
          <UiComponentParagraph class="citation">
            “{{ data.page.quote }}”
          </UiComponentParagraph>
          <UiComponentWrapper class="information">
            <UiComponentImage
              v-if="data.page.avatar"
              :alt="data.page.avatar.alt"
              class="avatar"
              :expected-size="48"
              :extra-params="{
                fit: 'crop',
                h: 48,
                w: 48,
              }"
              :height="data.page.avatar.height"
              :source="data.page.avatar.url"
              use-natural-height
              use-natural-width
              :width="data.page.avatar.width"
            />
            <UiComponentWrapper class="author">
              <UiComponentParagraph>{{ data.page.author }}</UiComponentParagraph>
              <UiComponentParagraph>{{ data.page.position }}</UiComponentParagraph>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <!-- End -->

        <!-- Share -->
        <UiComponentWrapper class="share">
          <UiComponentHeading
            as="paragraph"
            :level="5"
          >
            {{
              data.hub.shareThisArticle
            }}
          </UiComponentHeading>
          <UiComponentWrapper class="list">
            <AtomButton
              class="item"
              force-no-follow
              type="text"
              :to="`https://twitter.com/intent/tweet?text=${publicRuntimeConfig.baseUrl}${path}`"
              @click="
                track('Share clicked', {
                  category: 'Button',
                  label: 'Twitter content',
                })
              "
            >
              <UiComponentImage
                alt="Twitter"
                class="twitter"
                :expected-size="24"
                :height="24"
                source="https://livestorm.imgix.net/1127/1647943720-twitter.svg"
                :width="24"
              />
            </AtomButton>
            <AtomButton
              class="item"
              force-no-follow
              type="text"
              :to="`https://www.linkedin.com/sharing/share-offsite/?url=${publicRuntimeConfig.baseUrl}${path}`"
              @click="
                track('Share clicked', {
                  category: 'Button',
                  label: 'Linkedin content',
                })
              "
            >
              <UiComponentImage
                alt="Linkedin"
                class="linkedin"
                :expected-size="24"
                :height="24"
                source="https://livestorm.imgix.net/1127/1647943723-linkedin.svg"
                :width="24"
              />
            </AtomButton>
            <AtomButton
              class="item"
              force-no-follow
              type="text"
              :to="`http://www.facebook.com/sharer.php?u=${publicRuntimeConfig.baseUrl}${path}`"
              @click="
                track('Share clicked', {
                  category: 'Button',
                  label: 'Facebook content',
                })
              "
            >
              <UiComponentImage
                alt="Facebook"
                class="facebook"
                :expected-size="24"
                :height="24"
                source="https://livestorm.imgix.net/1127/1647943725-facebook.svg"
                :width="24"
              />
            </AtomButton>
            <AtomButton
              class="item"
              force-no-follow
              :to="mailto"
              type="text"
              @click="
                track('Share clicked', {
                  category: 'Button',
                  label: 'Email content',
                })
              "
            >
              <UiComponentImage
                alt="Email"
                class="email"
                :expected-size="24"
                :height="24"
                source="https://livestorm.imgix.net/1127/1673856867-mail.svg"
                :width="24"
              />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <!-- End -->

        <!-- Table of Content (Conditional) -->
        <!-- Displayed only when the device width >= 1024px  -->
        <UiComponentWrapper
          v-if="tableOfContentEntries.length > 1"
          class="table-of-content"
        >
          <UiComponentHeading
            as="paragraph"
            :level="5"
          >
            {{
              data.hub.tableOfContent
            }}
          </UiComponentHeading>
          <UiComponentSelect
            id="select"
            :key="`table-of-content-${tableOfContentIndex}`"
            :index="tableOfContentIndex"
            :options="
              tableOfContentEntries.map(({ textContent }) => textContent)
            "
            @select:change="
              scrollToTheSelectedElement(
                tableOfContentEntries.findIndex(
                  ({ textContent }) => textContent === $event,
                ),
              )
            "
          />
          <UiComponentWrapper class="list">
            <UiComponentParagraph
              v-for="(title, index) in tableOfContentEntries.map(
                ({ textContent }) => textContent,
              )"
              :key="`title-${index}`"
              :class="{ active: index === tableOfContentIndex }"
              @click="scrollToTheSelectedElement(index)"
            >
              {{ title }}
            </UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <!-- End -->

        <!-- Request Demo -->
        <UiComponentWrapper
          class="request-demo"
          @click="onRequestDemo"
        >
          <UiComponentImage
            v-if="
              data.componentGetStarted.background
                && getStartedBackgroundHeight
                && getStartedBackgroundWidth
            "
            :key="`${getStartedBackgroundHeight}x${getStartedBackgroundWidth}`"
            :alt="data.componentGetStarted.background.alt"
            class="background"
            :expected-size="getStartedBackgroundWidth"
            :extra-params="{
              fit: 'crop',
              h: getStartedBackgroundHeight,
              w: getStartedBackgroundWidth,
            }"
            :height="getStartedBackgroundHeight"
            :orientation="
              getStartedBackgroundHeight > getStartedBackgroundWidth
                ? 'portrait'
                : 'landscape'
            "
            :source="data.componentGetStarted.background.url"
            :width="getStartedBackgroundWidth"
          />
          <UiComponentWrapper class="inner">
            <UiComponentHeading
              as="paragraph"
              :level="5"
            >
              {{
                data.componentGetStarted.title
              }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ data.componentGetStarted.text }}</UiComponentParagraph>
            <AtomButton type="text">
              {{ requestDemoCallToAction.slot }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <!-- End -->

        <!-- Download (Conditional) -->
        <!-- Displayed only if the pdf exists -->
        <UiComponentWrapper
          v-if="data.page.downloadPdf"
          class="download"
        >
          <UiComponentHeading
            as="paragraph"
            :level="5"
          >
            {{
              data.page.downloadTitle
            }}
          </UiComponentHeading>
          <UiComponentParagraph v-if="data.page.downloadText">
            {{
              data.page.downloadText
            }}
          </UiComponentParagraph>
          <AtomButton
            action="Button clicked"
            label="Sidebar section CTA"
            size="l"
            to="#form-ebook"
          >
            <UiComponentIcon type="download" />{{ data.page.downloadCta }}
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <!-- End -->
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Related Content -->
    <UiComponentContainer
      v-if="resources.length > 0"
      class="related-content"
    >
      <UiComponentWrapper class="heading">
        <UiComponentParagraph>{{ data.hub.relatedResourcesTitle }}</UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="list">
        <OrganismContentCard
          v-for="content in resources"
          :key="content.id"
          class="item"
          :content="content"
          crop="top"
        />
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Get a Demo -->
    <OrganismGetADemo
      :content="data.componentDemo"
      :global="data.componentGlobal"
    />
    <!-- End -->
  </UiComponentWrapper>
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
  middleware: [
    'check-if-the-customer-story-exists',
  ],
})

// i18n
defineI18nRoute({
  locales: ['en', 'es', 'fr'],
  paths: {
    en: '/customers/[slug]',
    es: '/clientes/[slug]',
    fr: '/clients/[slug]',
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $interpolate, $slugify } = useNuxtApp()
const { setResource } = useResource()
const { meta, params, path } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Async Data
const { data } = await useFetch(`/api/content/pages/customers/${locale.value}`, {
  key: `page-customers-[slug]-${params.slug}-${locale.value}`,
  query: {
    preview: state.preview,
  },
  transform: (input) => {
    const { allCustomerStories, page: hub, ...props } = input
    const page = allCustomerStories.find(({ slug }) => slug === params.slug)
    return {
      ...props,
      hub,
      page,
    }
  },
})

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Meta
const alternativeVersions = data.value.page.alternativeVersions.filter(({ value }) => value.length > 0)
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale }) => ({
    ...accumator,
    [locale]: { slug: data.value.page.slug },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useSeoMeta({
  title: data.value.page.title,
  description: data.value.page.description,
  ogTitle: data.value.page.title,
  ogDescription: data.value.page.description,
  twitterTitle: data.value.page.title,
  twitterDescription: data.value.page.description,
})

useHead({
  link: head.value.link
    .filter((link) => {
      return link.rel === 'canonical'
        || (link.rel === 'alternate' && link.hreflang === 'x-default')
        || (link.rel === 'alternate' && locales.includes(link.hreflang))
    })
    .map((link) => {
      return {
        ...link,
        href: !locales.includes('en') && link.id === 'i18n-xd'
          ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
          : link.href,
      }
    }),
  meta: head.value.meta.filter((meta) => {
    return meta.property === 'og:url'
      || meta.property === 'og:locale'
      || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
  }),
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('customers')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.page.trackingBreadcrumbTitle || data.value.page.name,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// States
const forms = useState('forms')

// Refs
const getStartedBackgroundHeight = ref(null)
const getStartedBackgroundWidth = ref(null)
const getStartedObserver = ref(null)
const heroHeight = ref(null)
const heroObserver = ref(null)
const lastYPosition = ref(0)
const pageHeight = ref(null)
const requestAnimationFrameId = ref(null)
const tableOfContentEntries = ref([])
const tableOfContentIndex = ref(0)
const timeoutId = ref(null)

// Computed Properties
const contentImagesWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1024:
      return screenWidth.value - 48
    case screenWidth.value >= 1024 && screenWidth.value < 1328:
      return Math.round(840 * (screenWidth.value / 1328))
    case screenWidth.value >= 1328:
      return 840
    default:
      return 840
  }
})

const eventIframeHeight = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return 662
    case screenWidth.value >= 768:
      return 310
    default:
      return 310
  }
})

const heroImageHeight = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return 350
    case screenWidth.value >= 768 && screenWidth.value < 1024:
      return 521
    case screenWidth.value >= 1024:
      return heroHeight.value
    default:
      return 350
  }
})

const heroImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1024:
      return screenWidth.value - 48
    case screenWidth.value >= 1024 && screenWidth.value < 1328:
      return Math.round(510 * (screenWidth.value / 1328))
    case screenWidth.value >= 1328:
      return 510
    default:
      return 510
  }
})

const mailto = computed(() => {
  return `mailto:jane@doe.co?subject=${data.value.page.title}&body=Hey ! This article on Livestorm is interesting: ${publicRuntimeConfig.baseUrl}${path}`
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: data.value.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const readingProgressInPercentage = computed(() => {
  return pageHeight.value
    ? Math.min((lastYPosition.value / pageHeight.value) * 100, 100)
    : 0
})

const resources = computed(() => {
  return data.value.page.relatedResources
    .filter(({ slug }) => !!slug)
    .map((resource) => {
      if (resource.__typename === 'QuizRecord') {
        const type = data.value.allResourceTypes.find(
          type => type.name === 'Quiz',
        )

        return {
          id: resource.id,
          backgroundImage: resource.backgroundImage,
          callToAction: type.cta,
          description: resource.shortDescription,
          image: resource.image,
          tagColor: type.color,
          tagIcon: type.icon,
          title: resource.shortTitle,
          to: localePath({
            name: 'resources-quiz-slug',
            params: {
              slug: resource.slug,
            },
          }),
          topic: resource.resourceTopic.name,
          type: type.name,
        }
      }
      else if (resource.__typename === 'PillarPageRecord') {
        const type = data.value.allResourceTypes.find(
          type => type.name === 'Guides' || type.name === 'Guías',
        )

        return {
          id: resource.id,
          backgroundImage: resource.backgroundImage,
          callToAction: type.cta,
          description: resource.shortDescription,
          image: resource.image,
          tagColor: type.color,
          tagIcon: type.icon,
          title: resource.shortTitle,
          to: localePath({
            name: 'resources-guides-slug',
            params: {
              slug: resource.slug,
            },
          }),
          topic: resource.resourceTopic.name,
          type: type.name,
        }
      }
      else {
        return {
          id: resource.id,
          backgroundImage: resource.backgroundImage,
          callToAction: resource.resourceType?.cta,
          description: resource.shortDescription,
          image: resource.image,
          tagColor: resource.resourceType?.color,
          tagIcon: resource.resourceType?.icon,
          title: resource.shortTitle,
          to: resource.slug.includes('http')
            ? resource.slug
            : localePath({
                name: 'resources-type-slug',
                params: {
                  slug: resource.slug,
                  type: $slugify(resource.resourceType?.name),
                },
              }),
          topic: resource.resourceTopic.name,
          type: resource.resourceType?.name,
        }
      }
    })
})

const signUpOrUpdateCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaGetStartedForFree,
      }
})

// Methods
function getOffsetTop($el) {
  let curtop = 0
  if ($el.offsetParent) {
    do {
      curtop += $el.offsetTop
    } while (($el = $el.offsetParent))
    return curtop
  }
}

function isSticky(element) {
  if ('sticky' !== getComputedStyle(element).position) {
    return false
  }

  return (
    1
    >= element.getBoundingClientRect().top
    - parseInt(getComputedStyle(element).top)
  )
}

function onObserveHeroSection() {
  heroHeight.value = document.querySelector('.page .jumbotron .hero').offsetHeight
}

function onObserveRequestDemoSection() {
  const { offsetHeight, offsetWidth } = document.querySelector(
    '.page .content .aside .request-demo',
  )
  getStartedBackgroundHeight.value = offsetHeight
  getStartedBackgroundWidth.value = offsetWidth
}

function onRequestAnimationFrame() {
  const { scrollY } = window
  const activeEntry = tableOfContentEntries.value
    .map(({ offsetHeight, offsetTop }, index) => ({
      index,
      offsetHeight,
      offsetTop,
    }))
    .reverse()
    .find(
      ({ offsetHeight, offsetTop }) =>
        scrollY >= offsetTop - offsetHeight * 2,
    )

  if (activeEntry) {
    tableOfContentIndex.value = activeEntry.index
  }

  lastYPosition.value = scrollY
  pageHeight.value
    = document.documentElement.offsetHeight
      - Math.max(document.documentElement.clientHeight || window.innerHeight)

  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )
}

function onRequestDemo() {
  track('Demo clicked', {
    category: 'Button',
    label: 'Sidebar section CTA',
  })
  forms.value.demo.isVisible = true
}

function onScroll() {
  clearTimeout(timeoutId.value)
  timeoutId.value = setTimeout(() => {
    toggleSticky(document.querySelector('.page .aside'))
  }, 10)
}

function scrollToTheSelectedElement(index) {
  if (tableOfContentIndex.value === index) {
    return
  }

  const $h2 = tableOfContentEntries.value.find((_, i) => i === index)
  const top
    = getOffsetTop($h2)
      - document.querySelector('.app__layout-header').offsetHeight

  window.scrollTo({
    top,
    behavior: 'smooth',
  })

  tableOfContentIndex.value = index
}

function toggleSticky(element) {
  if (isSticky(element)) {
    element.classList.add('is-stuck')
  }
  else {
    element.classList.remove('is-stuck')
  }
  return element
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (heroObserver.value) {
    heroObserver.value.unobserve(document.querySelector('.page .jumbotron .hero'))
  }

  if (getStartedObserver.value) {
    getStartedObserver.value.unobserve(document.querySelector('.page .content .aside .request-demo'))
  }

  if (requestAnimationFrameId.value) {
    window.cancelAnimationFrame(requestAnimationFrameId.value)
  }

  window.removeEventListener('scroll', onScroll)
})

onMounted(() => {
  window.addEventListener('scroll', onScroll)

  heroObserver.value = new ResizeObserver(onObserveHeroSection)
  heroObserver.value.observe(document.querySelector('.page .jumbotron .hero'))

  getStartedObserver.value = new ResizeObserver(onObserveRequestDemoSection)
  getStartedObserver.value.observe(
    document.querySelector('.page .content .aside .request-demo'),
  )

  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )

  tableOfContentEntries.value = [
    ...document.querySelectorAll('.page .content .main .section.text h2'),
  ]
  page(data.value.page.trackName || data.value.page.name)
  setAvailableLanguages(
    data.value.page.alternativeVersions
      .filter(({ value }) => value.length > 0)
      .map(({ locale }) => locale),
  )
  toggleSticky(document.querySelector('.page .aside'))
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('customers'),
      name: data.value.hub.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || data.value.page.name,
    },
  ])

  if (data.value.page.downloadPdf) {
    setResource({
      id: data.value.page.id,
      backgroundImage: data.value.page.image,
      callToAction: data.value.page.downloadCta,
      coMarketing: false,
      description: data.value.page.title,
      event: {
        name: 'Offer clicked',
        attributes: {
          label: 'Thank you pop up CTA',
        },
      },
      image: {
        download: true,
      },
      tagColor: 'livestorm-blue',
      tagIcon: {
        name: 'book',
      },
      title: `Customer story - ${data.value.page.name}`,
      to: data.value.page.downloadPdf.url,
      topic: null,
      type: data.value.hub.popupTag,
      url: data.value.page.downloadPdf.url,
    })
  }
})
</script>

<style lang="postcss" scoped>
.page {
  @apply relative flex-col items-center;

  .progress {
    @apply fixed left-0 top-13 h-[5px] w-full z-20;

    @screen xl {
      @apply top-[72px];
    }

    .bar {
      @apply h-full w-0 bg-livestorm-blue-700 rounded-r-[3px] transition-none;
    }
  }

  .jumbotron {
    @apply flex-col pt-12;

    @screen lg {
      @apply flex-row flex-wrap justify-between py-10;
    }

    &.centered {
      @screen lg {
        @apply items-center;
      }

      .media {
        .picture,
        .video {
          @apply shadow-light2;
        }
      }
    }

    .hero {
      @apply flex-col;

      @screen lg {
        @apply w-span6;
      }

      .back {
        @apply flex self-start items-center w-auto px-2 py-1 bg-livestorm-blue-100 font-medium text-livestorm-blue-700 text-xs leading-[15.6px] rounded-xs;

        i {
          @apply mr-1 text-livestorm-blue-700 text-xs leading-none;
        }
      }

      .logo {
        @apply mt-6 rounded-none;
      }

      .h1 {
        @apply mt-6 text-3xl leading-10;

        @screen mdminus {
          @apply text-3xl leading-10;
        }

        @screen mdplus {
          @apply text-5xl leading-16;
        }
      }

      .h1 + p {
        @apply mt-4 text-grey-blue-600 text-origin leading-7;
      }

      .information {
        @apply flex-wrap mt-6 pt-6 pb-2 border-t border-grey-blue-200;

        @screen md {
          @apply justify-between;
        }

        .item {
          @apply flex-col w-1/2;

          @screen md {
            @apply w-auto;
          }

          &:nth-child(3),
          &:nth-child(4) {
            @apply mt-4;

            @screen md {
              @apply mt-0;
            }
          }

          p {
            @apply font-medium text-grey-blue-600 text-sm leading-7;
          }

          p + p {
            @apply font-medium text-winter-green-900 text-base leading-[21px];
          }
        }
      }
    }

    .media {
      @apply mt-10;

      @screen lg {
        @apply w-span5 mt-0;
      }

      .picture {
        @apply rounded-3xl;

        @screen md {
          @apply rounded-4xl;
        }
      }

      .video {
        @apply flex self-start h-auto;

        :deep(video) {
          @apply self-start h-auto !important;
        }
      }
    }
  }

  .content {
    @apply flex-col mt-14;

    @screen md {
      @apply mt-26;
    }

    @screen lg {
      @apply flex-row flex-wrap justify-between;
    }

    .header {
      @apply flex-col;

      @screen lg {
        @apply hidden;
      }

      p {
        @apply font-medium text-winter-green-900 text-sm leading-6;
      }
    }

    .header + .main {
      @apply mt-16;

      @screen lg {
        @apply mt-0;
      }
    }

    .main {
      @apply flex-col;

      @screen lg {
        @apply w-span8;
      }

      .section {
        @apply mt-10;

        &:first-child {
          @apply mt-0;
        }

        &.features {
          .inner {
            @apply relative flex-col p-8 bg-live-red-100 rounded-4xl overflow-hidden;

            @screen md {
              @apply flex-row flex-wrap justify-between p-10;
            }

            .background {
              @apply absolute bottom-0 left-0 z-0;
            }

            .h4 {
              @apply w-full z-10;

              @screen md {
                max-width: calc((100% - 40px) / 2);
              }

              @screen lg {
                @apply max-w-[280px];
              }
            }

            .list {
              @apply flex-col mt-8 z-10;

              @screen md {
                @apply mt-0;
                max-width: calc((100% - 40px) / 2);
              }

              @screen lg {
                max-width: calc(100% - 344px);
              }

              .item {
                @apply flex-col mt-6;

                &:first-child {
                  @apply mt-0;
                }

                .h5 + p {
                  @apply text-grey-blue-600 text-origin leading-7;
                }
              }

              .button {
                @apply self-start h-[50px] mt-6 px-0 font-bold;
              }
            }
          }
        }

        &.get-started {
          @apply mt-10;
        }

        &.image {
          @apply bg-grey-blue-100 rounded-4xl overflow-hidden;

          &:deep(picture) {
            @apply rounded-none;
          }
        }

        &.image + .tag {
          @apply mt-16;
        }

        &.key-numbers {
          .inner {
            @apply relative flex-col p-8 bg-live-red-100 rounded-4xl overflow-hidden;

            @screen md {
              @apply p-10;
            }

            .background {
              @apply hidden absolute top-0 right-0 z-0;

              @screen md {
                @apply flex;
              }
            }

            .h4 {
              @apply w-full z-10;
            }

            .list {
              @apply flex-col mt-2 z-10;

              @screen md {
                @apply flex-row flex-wrap mt-0;
              }

              .item {
                @apply flex-col mt-6 p-4 bg-sirocco-200 rounded-2xl;

                @screen md {
                  @apply flex-row items-center w-auto mt-8 mr-8;
                }

                p {
                  @apply font-bold text-sirocco-800 text-2xl leading-[31px];
                }

                p + p {
                  @apply font-medium text-grey-blue-800 text-origin;

                  @screen md {
                    @apply ml-2;
                  }
                }
              }
            }
          }
        }

        &.product-feature {
          @apply mt-10;
        }

        &.quote {
          .inner {
            @apply p-8 bg-livestorm-blue-100 rounded-4xl;

            @screen md {
              @apply p-10;
            }

            div {
              @apply text-center text-livestorm-blue-700 text-xl leading-[160%];

              &::before,
              &::after {
                @apply inline;
              }

              &::before {
                @apply content-['“'];
              }

              &::after {
                @apply content-['”'];
              }

              :deep(p) {
                @apply inline;
              }
            }
          }
        }

        &.tag + .text {
          @apply mt-6;
        }

        &.text {
          .inner {
            @apply flex flex-col;

            :deep(a) {
              @apply font-normal text-livestorm-blue-700;
            }

            :deep(b),
            :deep(strong) {
              @apply font-medium;
            }

            :deep(blockquote) {
              @apply p-8 bg-livestorm-blue-100 rounded-4xl;

              @screen md {
                @apply p-10;
              }

              p {
                @apply inline text-center text-livestorm-blue-700 text-xl leading-[160%];

                &::before,
                &::after {
                  @apply inline;
                }

                &::before {
                  @apply content-['“'];
                }

                &::after {
                  @apply content-['”'];
                }
              }
            }

            :deep(p + blockquote),
            :deep(h2 + blockquote),
            :deep(h3 + blockquote),
            :deep(h4 + blockquote),
            :deep(h5 + blockquote),
            :deep(h6 + blockquote),
            :deep(blockquote + p),
            :deep(blockquote + h2),
            :deep(blockquote + h3),
            :deep(blockquote + h4),
            :deep(blockquote + h5),
            :deep(blockquote + h6) {
              @apply mt-6;
            }

            :deep(h2),
            :deep(h3),
            :deep(h4),
            :deep(h5),
            :deep(h6) {
              @apply text-winter-green-900 whitespace-pre-line;
            }

            :deep(h2) {
              @apply font-normal;

              b,
              strong {
                @apply font-normal text-spring-green-600;

                @screen mdminus {
                  @apply font-medium;
                }

                @screen mdplus {
                  @apply font-normal;
                }
              }
            }

            :deep(h3) {
              @apply font-medium;

              b,
              strong {
                @apply font-medium text-winter-green-600;
              }
            }

            :deep(h4),
            :deep(h5),
            :deep(h6) {
              @apply font-medium;

              b,
              strong {
                @apply font-medium text-livestorm-blue-700;
              }
            }

            :deep(h2) {
              @apply text-3xl leading-10;

              @screen mdminus {
                @apply font-medium text-3xl leading-10;
              }

              @screen mdplus {
                @apply font-normal text-5xl leading-16;
              }
            }

            :deep(h3) {
              @apply text-2xl leading-8;

              @screen mdminus {
                font-size: 28px;
                line-height: 34px;
              }

              @screen mdplus {
                @apply text-3xl leading-10;
              }
            }

            :deep(h4) {
              @apply text-xl leading-7;

              @screen mdminus {
                @apply text-2xl leading-8;
              }

              @screen mdplus {
                @apply text-2xl leading-8;
              }
            }

            :deep(h5) {
              @apply text-origin leading-7;

              @screen mdminus {
                @apply text-xl;
              }

              @screen mdplus {
                @apply text-xl;
              }
            }

            :deep(h6) {
              @apply text-origin leading-7;
            }

            :deep(ol) {
              @apply ml-8;

              li {
                @apply font-normal text-grey-blue-600 text-origin leading-7 list-decimal list-inside;
              }
            }

            :deep(ul) {
              @apply ml-8;

              li {
                @apply block mt-3 font-normal text-grey-blue-600 text-origin leading-7;

                &:first-child {
                  @apply mt-0;
                }

                &::before {
                  @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-4 rounded-[3px] content-[''];
                }
              }
            }

            :deep(p) {
              @apply text-grey-blue-600 text-origin leading-7 whitespace-pre-wrap;
            }

            :deep(h2 + p),
            :deep(h3 + p),
            :deep(h4 + p),
            :deep(h5 + p),
            :deep(h6 + p),
            :deep(h2 + ol),
            :deep(h3 + ol),
            :deep(h4 + ol),
            :deep(h5 + ol),
            :deep(h6 + ol),
            :deep(h2 + ul),
            :deep(h3 + ul),
            :deep(h4 + ul),
            :deep(h5 + ul),
            :deep(h6 + ul),
            :deep(p + h2),
            :deep(p + h3),
            :deep(p + h4),
            :deep(p + h5),
            :deep(p + h6),
            :deep(p + p),
            :deep(p + ol),
            :deep(p + ul),
            :deep(ol + h2),
            :deep(ol + h2),
            :deep(ol + h4),
            :deep(ol + h5),
            :deep(ol + h6),
            :deep(ol + p),
            :deep(ul + h2),
            :deep(ul + h2),
            :deep(ul + h4),
            :deep(ul + h5),
            :deep(ul + h6),
            :deep(ul + p) {
              @apply mt-4;
            }
          }
        }

        &.tl-dr {
          .inner {
            @apply flex-col p-8 bg-grey-blue-100 border-2 border-grey-blue-200 rounded-4xl;

            @screen md {
              @apply p-10;
            }

            :deep(div) {
              @apply mt-4 text-grey-blue-600 text-origin leading-7;

              a {
                @apply text-livestorm-blue-700 transition-colors duration-300 ease-in-out;

                &:hover {
                  @apply text-livestorm-blue-800;
                }
              }
            }
          }
        }

        &.video {
          .inner {
            @apply relative pb-16/9 rounded-4xl overflow-hidden;

            iframe {
              @apply absolute inset-0;
            }
          }
        }
      }
    }

    .aside {
      @apply flex-col mt-16;

      @screen lg {
        @apply sticky top-24 self-start w-span3 mt-0;
      }

      &.is-stuck {
        .quote {
          @apply -mt-[100%] opacity-0 invisible;
        }

        .table-of-content {
          @apply mt-6;

          .h5 {
            @apply text-sm leading-6;
          }

          .select {
            @apply max-h-max mt-1 opacity-100 visible;
          }

          .list {
            @apply max-h-0 mt-0 opacity-0 invisible;
          }
        }

        .share {
          @apply max-h-0 mt-0 opacity-0 invisible;
        }
      }

      .quote {
        @apply flex-col p-6 border-2 border-grey-blue-200 rounded-3xl transition-all duration-500 ease-in-out;

        @screen lg {
          @apply p-8 rounded-4xl;
        }

        .citation {
          @apply text-grey-blue-600 text-sm leading-6;
        }

        .information {
          @apply flex-wrap items-center mt-4;

          .avatar {
            @apply flex-shrink-0 rounded-full mr-3;
          }

          .author {
            @apply flex-col;
            width: calc(100% - 60px);

            p {
              @apply font-bold text-winter-green-900 text-sm leading-6;
            }

            p + p {
              @apply w-full font-normal text-grey-blue-600 text-sm leading-5;
            }
          }
        }
      }

      .share {
        @apply flex-col mt-10 transition-all duration-500 ease-in-out;

        .list {
          @apply mt-2;

          .item {
            @apply self-start h-6 w-6 mx-3 px-0;

            &:first-child {
              @apply ml-0;
            }

            &:last-child {
              @apply mr-0;
            }

            .email,
            .twitter,
            .facebook,
            .linkedin {
              @apply rounded-none;
              filter: brightness(0) saturate(100%) invert(39%) sepia(36%)
                saturate(210%) hue-rotate(163deg) brightness(96%) contrast(88%);
            }
          }
        }
      }

      .table-of-content {
        @apply hidden;

        @screen lg {
          @apply flex flex-col mt-10 transition-all duration-500 ease-in-out;
        }

        .h5 {
          @apply transition-all duration-500 ease-in-out;
        }

        .select {
          @apply max-h-0 mt-0 opacity-0 invisible transition-all duration-500 ease-in-out;
        }

        .list {
          @apply flex-col mt-4 max-h-max border-t border-grey-blue-200 transition-all duration-500 ease-in-out;

          p {
            @apply mt-4 text-grey-blue-600 text-sm leading-6 cursor-pointer transition-all duration-600 ease-in-out;

            &.active {
              @apply ml-2 text-grey-blue-900 cursor-default;
            }
          }
        }
      }

      .request-demo {
        @apply relative mt-10 p-8 rounded-4xl shadow-light4 cursor-pointer;

        &:hover {
          .inner {
            .button {
              :deep(i) {
                @apply translate-x-2;
              }
            }
          }
        }

        .background {
          @apply absolute inset-0 bg-livestorm-blue-700 rounded-4xl z-0;
        }

        .inner {
          @apply flex-col z-10;

          .h5 {
            @apply text-white;
          }

          .h5 + p {
            @apply mt-4 text-white text-origin leading-7;
          }

          .button {
            @apply self-start h-[50px] mt-2 px-0 font-bold text-white;

            i {
              @apply text-white text-base leading-none;
            }
          }
        }
      }

      .download {
        @apply flex-col mt-10 p-8 border-2 border-grey-blue-200 rounded-4xl;

        .h5 {
          @apply text-winter-green-900;
        }

        .h5 + p {
          @apply mt-4 text-grey-blue-600 text-origin leading-7;
        }

        .button {
          @apply mt-6;

          i {
            @apply mr-2 text-white text-base leading-none;
          }
        }
      }
    }
  }

  .related-content {
    @apply flex-col justify-center mt-26;

    @screen md {
      @apply mt-46;
    }

    .heading {
      @apply relative justify-center items-center;

      &::before {
        @apply absolute h-[2px] w-full bg-grey-blue-200 content-[''];
      }

      p {
        @apply px-4 bg-white font-medium text-[#4D5B66] text-sm leading-6 uppercase z-10;
      }
    }

    .list {
      @apply flex-col mt-8;

      @screen xl {
        @apply flex-row flex-wrap items-stretch justify-between;
      }

      .item {
        @apply mt-10;

        @screen xl {
          @apply flex-col h-[unset] mt-0;
          width: calc(calc(100% - 5rem) / 3);
        }

        &:first-child {
          @apply mt-0;
        }

        :deep(.head) {
          @screen xl {
            height: 400px;
            width: 400px;
          }
        }

        :deep(.body) {
          @screen xl {
            @apply justify-start h-full p-6;
          }

          a {
            @screen xl {
              @apply mb-auto;
            }
          }

          p {
            @apply hidden !important;
          }
        }
      }
    }
  }

  .get-a-demo {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }
  }
}
</style>
